import axios from "axios";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { Box } from "@mui/material";
import Tile from "../../Components/Tiles/Tile";
import TileContent from "../../Components/Tiles/TileContent";
import TileContentActions from "../../Components/Tiles/TileContentActions";
import TileContentAction from "../../Components/Tiles/TileContentAction";
import DashboardKpi, { kpiPlaceholder } from "../../Components/Dashboard/DashboardKpi";
import formatNumber from "../../system/formatNumber";
import useAsyncEffect from "../../system/useAsyncEffect";
import useUser from "../../system/useUser";
import { UserRole, WorkSession } from "../../system/Domain";
import { setWorkSession } from "../../system/reducers/workSessions/actions";
import compoundIdsQuery from "./compoundIdsQuery";

interface CompoundMissingFirstPhotosTileProps {
	compoundIds: string[];
}

interface MissingFirstPhotosKpi {
	missingFirstPhotos: number;
}

export default ({ compoundIds }: CompoundMissingFirstPhotosTileProps) => {
	const dispatch = useDispatch();
	const [, , hasRole] = useUser();
	const canStartSessions = hasRole(UserRole.AldUser);
	const [data, setData] = useState<MissingFirstPhotosKpi>();
	// react's dependency comparison is by reference; compoundIds is a new array on every parent
	// render, so depend on a stable joined string instead to avoid an infinite fetch loop.
	const compoundIdsKey = compoundIds.join(",");

	useAsyncEffect(async () => {
		if (compoundIds.length === 0) {
			setData({ missingFirstPhotos: 0 });
			return;
		}

		const { data } = await axios.get<MissingFirstPhotosKpi>(
			`/api/compounds/kpis/missing-first-photos?${compoundIdsQuery(compoundIds)}`
		);
		setData(data);
	}, [compoundIdsKey]);

	const createWorkSession = async () => {
		const { data: workSession } = await axios.post<WorkSession>(`/api/work-sessions/presales`, {
			perspective: "CompoundMissingFirstPictureServices",
			title: "Fehlende Erstfotosätze",
			compoundIds
		});

		if (workSession) {
			dispatch(setWorkSession(workSession));
		}
	};

	return (
		<Tile title="Fehlende Erstfotosätze">
			<TileContent dense>
				<Box p={2}>
					{data && (
						<DashboardKpi
							primary={formatNumber(data.missingFirstPhotos)}
							secondary={undefined}
						/>
					)}
					{!data && kpiPlaceholder}
				</Box>
				{canStartSessions && (
					<TileContentActions align="right">
						<TileContentAction
							disabled={!data || data.missingFirstPhotos === 0}
							onClick={createWorkSession}
							label="Überprüfen"
						/>
					</TileContentActions>
				)}
			</TileContent>
		</Tile>
	);
};
